// PARAQTA — Профиль v2 (Опус 4.7 дизайн).
//
// loadProfile() — fetch /api/profile/me, populate [data-field],
// apply [data-visibility] (premium-only / free-only),
// generate heatmap (45 күн) + reading_now + achievements + quotes.
//
// 04.10: ескі stats беті (loadStatsPage) өшірілді — «Оқу шамы» виджеті толық қайталайтын.

import { api } from '../../core/api.js';
import { show } from '../../core/router.js';
import { openBookPage } from '../book-page/index.js';
import { toast } from '../../utils/toast.js';
import { esc } from '../../utils/format.js';

const $ = (sel, r = document) => r.querySelector(sel);
const $$ = (sel, r = document) => Array.from(r.querySelectorAll(sel));
const root = () => document.getElementById('pg-profile');

function setField(name, value) {
  const el = $(`[data-field="${name}"]`, root());
  if (el) el.textContent = value;
}

function activityColor(activity) {
  // Stage 6.8.2 founder bug: empty-day 4% ink dark mode-та шығармайды
  // (cream tint dark bg-да көрінбейді). 12%-ке өсіріп екі mode-та да visible.
  if (!activity || activity <= 0) return 'color-mix(in srgb, var(--ink) 12%, transparent)';
  return `color-mix(in srgb, var(--ac) ${Math.round(Number(activity) * 100)}%, transparent)`;
}

function applyVisibility(data) {
  const sub = data?.user?.subscription_status || 'free';
  const premium = (sub === 'jandos' || sub === 'family_member');
  $$('[data-visibility]', root()).forEach(el => {
    const rule = el.dataset.visibility;
    let visible = true;
    if (rule === 'free-only') visible = (sub === 'free');
    else if (rule === 'premium-only') visible = premium;
    else if (rule === 'family-member-only') visible = (sub === 'family_member');
    el.style.display = visible ? '' : 'none';
  });
}

function renderHero(data) {
  const u = data.user || {};
  // Stage 7.5: avatar — egerimage URL bar, фон ретінде; жоқ болса — initials
  const avEl = $('[data-field="user-avatar"]', root());
  if (avEl) {
    if (u.avatar_url) {
      avEl.style.backgroundImage = `url(${u.avatar_url})`;
      avEl.style.color = 'transparent';
      avEl.textContent = '';
    } else {
      avEl.style.backgroundImage = '';
      avEl.style.color = 'var(--bg)';
      avEl.textContent = u.initials || '—';
    }
  }
  setField('user-name', u.name || 'Оқырман');
  setField('user-username', u.username ? `@${u.username}` : '');

  if (u.custom_url) {
    setField('user-url', u.custom_url);
  }

  // Stage 6.10 — Custom URL widget data
  const slug = u.custom_slug || '';
  const slugText = slug ? `paraqta.kz/u/${slug}` : 'Орнатылмаған';
  const slugCta = slug ? 'Өзгерту' : 'Орнату';
  setField('custom-slug-text', slugText);
  setField('custom-slug-cta', slugCta);
  const trigger = $('[data-field="custom-slug-trigger"]', root());
  if (trigger) trigger.dataset.currentSlug = slug;

  const lv = data.level || {};
  setField('level-current', lv.current || '—');
  setField('level-num', lv.num ?? 1);
  setField('level-progress-pct', lv.progress_pct ?? 0);
  // Ағымдағы жалпы ұпай (мыңдықты бос орынмен бөлеміз: 1 250)
  setField('level-points', (lv.total_points ?? 0).toLocaleString('kk-KZ').replace(/,/g, ' '));
  const bar = $('[data-field="level-progress-bar"]', root());
  if (bar) bar.style.width = `${lv.progress_pct ?? 0}%`;

  const nextLine = $('[data-field="level-next-line"]', root());
  if (nextLine) {
    nextLine.style.display = '';
    // Қазақ тіліндегі септік жалғауы — соңғы дауыссыз/дауысты бойынша
    // «-ге/-ке» (жіңішке: е,ө,ү,і,ə) немесе «-ға/-қа» (жуан: а,о,ұ,ы)
    // Кітап Падишасы → «-на» (дауысты я)
    const kazSuffix = (name) => {
      if (!name) return 'ға';
      const last = name.replace(/\s+/g, '').slice(-1).toLowerCase();
      const softV = ['е','ө','ү','і','э','ə'];
      const vowels = ['а','е','и','о','ө','у','ү','ы','і','э','ə','я','ю'];
      if (vowels.includes(last)) return 'на'; // дауыстыға аяқталады
      return softV.some(v => name.toLowerCase().includes(v)) ? 'ге' : 'ға';
    };
    if (lv.next) {
      const pts = (lv.points_to_next ?? 0).toLocaleString('kk-KZ').replace(/,/g, ' ');
      const suf = kazSuffix(lv.next);
      setField('level-next-full', `${lv.next}${suf} дейін ${pts} ұпай`);
    } else {
      setField('level-next-full', 'жоғарғы деңгей ✓');
    }
  }
}

function renderStats(data) {
  const s = data.stats || {};
  setField('stat-streak', s.streak ?? 0);
  setField('stat-books', s.books_finished ?? 0);
  setField('stat-pages', (s.total_pages ?? 0).toLocaleString('kk-KZ').replace(/,/g, ' '));
  setField('stat-quotes', s.quotes_saved ?? 0);
  setField('streak-rank', s.leaderboard_rank ? `🏆 #${s.leaderboard_rank} рейтингте` : '');
}

function renderReadingNow(data) {
  const list = Array.isArray(data.reading_now) ? data.reading_now : [];
  setField('reading-count', list.length);
  const wrap = $('[data-field="reading-list"]', root());
  if (!wrap) return;
  wrap.innerHTML = '';

  list.forEach(b => {
    const card = document.createElement('div');
    card.style.cssText = 'flex: 0 0 240px; background: var(--card); border-radius: 14px; padding: 14px; border: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent); cursor: pointer;';
    if (b.slug) {
      card.dataset.action = 'open-book';
      card.dataset.slug = b.slug;
    }
    card.dataset.bookId = b.book_id;
    const coverInner = b.cover_url
      ? `<div style="position: absolute; inset: 0; background: url(${esc(b.cover_url)}) center/cover no-repeat;"></div>`
      : `<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-family: Georgia, serif; font-size: 7px; font-style: italic; color: var(--bg); padding: 0 3px; text-align: center;">${esc(b.cover_text || b.title || '')}</div>`;
    card.innerHTML = `
      <div style="display: flex; gap: 12px; margin-bottom: 12px;">
        <div style="width: 52px; height: 76px; background: linear-gradient(135deg, #8B5E34 0%, #6B4423 100%); border-radius: 4px; flex-shrink: 0; position: relative; overflow: hidden;">
          ${coverInner}
        </div>
        <div style="flex: 1; min-width: 0;">
          <div style="font-family: Georgia, serif; font-size: 13px; font-weight: 500; line-height: 1.2; margin-bottom: 2px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${esc(b.title || '—')}</div>
          <div style="font-size: 10px; opacity: 0.55; margin-bottom: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${esc(b.author || '')}</div>
          <div style="font-size: 10px; opacity: 0.6;">${b.current_page || 0} / ${b.total_pages || 0} бет · <span style="color: var(--gold);">${b.progress_pct || 0}%</span></div>
        </div>
      </div>
      <div style="height: 3px; background: color-mix(in srgb, var(--ink) 6%, transparent); border-radius: 99px; overflow: hidden;">
        <div style="height: 100%; width: ${b.progress_pct || 0}%; background: var(--ac);"></div>
      </div>
    `;
    wrap.appendChild(card);
  });

  // "+ Кітап қосу" батырмасы (соңында)
  const add = document.createElement('div');
  add.dataset.action = 'add-book';
  add.style.cssText = 'flex: 0 0 80px; background: rgba(255,255,255,0.5); border-radius: 14px; padding: 14px; border: 0.5px dashed color-mix(in srgb, var(--ac) 30%, transparent); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; text-align: center; min-height: 130px; cursor: pointer;';
  add.innerHTML = `
    <i class="ti ti-plus" style="font-size: 18px; color: var(--ac);"></i>
    <div style="font-size: 10px; color: var(--ac);">Кітап<br/>қосу</div>
  `;
  wrap.appendChild(add);
}

function renderLists(data) {
  const l = data.lists || {};
  setField('list-finished-count', l.finished_count ?? 0);
  setField('list-reading-count', l.reading_count ?? 0);
  setField('list-wishlist-count', l.wishlist_count ?? 0);
}

// Kazakh month abbreviations (genitive used as suffix: "16 сәу")
const KK_MONTH_ABBR = ['қаң', 'ақп', 'нау', 'сәу', 'мам', 'мау', 'шіл', 'там', 'қыр', 'қаз', 'қар', 'жел'];

function _isoMonday(d) {
  // Convert JS Sunday=0..Saturday=6 → ISO Mon=1..Sun=7
  const iso = d.getDay() === 0 ? 7 : d.getDay();
  const m = new Date(d);
  m.setDate(d.getDate() - (iso - 1));
  m.setHours(0, 0, 0, 0);
  return m;
}

function _fmtDM(d) {
  return `${d.getDate()} ${KK_MONTH_ABBR[d.getMonth()]}`;
}

function _localISO(d) {
  // Local YYYY-MM-DD (toISOString() UTC-ге ауысады — backend күндерімен матч етпейді).
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ─── Stage 7.5 Session 2: Period-aware unified stats widget ───
const PERIOD_LABELS = { today: 'Бүгін', week: 'Апта', month: 'Ай', year: 'Жыл', all: 'Бәрі' };
const PERIOD_ORDER = ['today', 'week', 'month', 'year', 'all'];
const LOCKED_PERIODS = new Set(['month', 'year', 'all']);
const LVL_BG = [
  'color-mix(in srgb, var(--ink) 4%, transparent)',
  'color-mix(in srgb, #8B5E34 18%, transparent)',
  'color-mix(in srgb, #8B5E34 42%, transparent)',
  'color-mix(in srgb, #8B5E34 65%, transparent)',
  'linear-gradient(135deg, #C9A96E, #8B5E34)',
];
const LVL_COLOR = [
  'color-mix(in srgb, var(--ink) 40%, transparent)',
  '#8B5E34', '#fff', '#fff', '#fff',
];

let _statsState = { period: 'week', premium: false, cache: {} };

const CANDLE_SVG = `
  <svg viewBox="0 0 78 104" style="width:100%;height:100%;" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hm-wax" x1="0" x2="1" y1="0" y2="0">
        <stop offset="0%" stop-color="#D8B987"/>
        <stop offset="50%" stop-color="#C9A96E"/>
        <stop offset="100%" stop-color="#8B5E34"/>
      </linearGradient>
      <radialGradient id="hm-flame" cx="0.5" cy="0.6" r="0.6">
        <stop offset="0%" stop-color="#FFF8E0"/>
        <stop offset="40%" stop-color="#FFE599"/>
        <stop offset="80%" stop-color="#E9A949"/>
        <stop offset="100%" stop-color="#8B5E34" stop-opacity="0.6"/>
      </radialGradient>
    </defs>
    <ellipse cx="39" cy="100" rx="18" ry="2.5" fill="#8B5E34" opacity="0.22"/>
    <rect x="26" y="38" width="26" height="60" rx="3" fill="url(#hm-wax)"/>
    <rect x="28" y="40" width="3" height="55" rx="1.5" fill="#FBF7EF" opacity="0.4"/>
    <line x1="39" y1="38" x2="39" y2="28" stroke="#1C1A16" stroke-width="1.4"/>
    <ellipse cx="39" cy="20" rx="9" ry="14" fill="url(#hm-flame)"/>
    <ellipse cx="39" cy="22" rx="4" ry="8" fill="#FFF8E0"/>
  </svg>
`;

function renderHeatmap(data) {
  // Stage 7.5 Session 2: Period-aware unified stats widget.
  const grid = $('[data-field="heatmap-grid"]', root());
  const legend = $('[data-field="heatmap-legend"]', root());
  if (!grid) return;

  const sub = data?.user?.subscription_status || data?.user?.sub_status || '';
  const premium = (sub === 'jandos' || sub === 'family_member' || data?.is_premium === true);
  _statsState = { period: 'week', premium, cache: {} };

  grid.style.display = 'block';
  grid.style.gridTemplateColumns = 'none';
  grid.style.gap = '0';

  // Skeleton
  const pills = PERIOD_ORDER.map(p => {
    const lockIcon = (!premium && LOCKED_PERIODS.has(p))
      ? '<span style="font-size:9px;margin-left:3px;opacity:.7;">🔒</span>' : '';
    return `<button type="button" data-period="${p}" style="flex:1;padding:7px 0;font-size:11.5px;font-weight:600;text-align:center;color:color-mix(in srgb,var(--ink) 55%,transparent);border-radius:999px;cursor:pointer;border:none;background:transparent;font-family:Onest,sans-serif;transition:all .2s;">${PERIOD_LABELS[p]}${lockIcon}</button>`;
  }).join('');

  grid.innerHTML = `
    <div data-field="stats-periods" style="display:flex;gap:4px;background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:999px;padding:4px;margin-bottom:10px;">
      ${pills}
    </div>
    <div data-field="stats-candle"></div>
    <div data-field="stats-body" style="position:relative;">
      <div data-field="stats-content"></div>
      <div data-field="stats-overlay" style="display:none;position:absolute;inset:0;align-items:flex-start;justify-content:center;padding-top:80px;z-index:10;"></div>
    </div>
  `;
  if (legend) legend.innerHTML = '';

  // Wire pills
  $('[data-field="stats-periods"]', grid).addEventListener('click', (e) => {
    const btn = e.target.closest('[data-period]');
    if (!btn) return;
    setStatsPeriod(btn.dataset.period);
  });

  // Initial fetch
  setStatsPeriod('week');
}

async function setStatsPeriod(period) {
  _statsState.period = period;
  const grid = $('[data-field="heatmap-grid"]', root());
  if (!grid) return;

  // Highlight active pill
  $$('[data-period]', grid).forEach(b => {
    const active = b.dataset.period === period;
    b.style.background = active ? 'var(--ac)' : 'transparent';
    b.style.color = active ? '#fff' : 'color-mix(in srgb,var(--ink) 55%,transparent)';
  });

  // Fetch (with simple cache)
  let resp = _statsState.cache[period];
  if (!resp) {
    try {
      resp = await api(`/api/profile/stats?period=${period}`);
      if (resp && resp.candle) _statsState.cache[period] = resp;
    } catch (e) {
      console.warn('stats fetch failed', e);
      return;
    }
  }
  if (!resp || !resp.candle) return;

  renderStatsCandle(resp.candle);
  renderStatsBody(resp);
  renderStatsSummary(resp.summary);
}

function renderStatsCandle(c) {
  const wrap = $('[data-field="stats-candle"]', root());
  if (!wrap || !c) return;
  const lblHtml = c.hlbl ? `<div style="font-size:13px;color:var(--ac);font-weight:600;">${esc(c.hlbl)}</div>` : '';
  const numSize = (c.hnum && c.hnum.length >= 5) ? '26px' : '34px';
  wrap.innerHTML = `
    <div style="background:linear-gradient(155deg,#FBF7EF,#F1E9D8);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:16px;padding:18px;margin-bottom:10px;display:flex;align-items:center;gap:16px;">
      <div style="width:78px;height:104px;flex-shrink:0;position:relative;display:flex;align-items:flex-end;justify-content:center;">
        <div style="position:absolute;top:6px;left:50%;transform:translateX(-50%);width:60px;height:60px;background:radial-gradient(circle,rgba(233,169,73,.35) 0%,transparent 65%);pointer-events:none;"></div>
        ${CANDLE_SVG}
      </div>
      <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
        <div style="display:flex;align-items:baseline;gap:8px;">
          <div style="font-family:'PT Serif',Georgia,serif;font-size:${numSize};font-weight:600;color:var(--ac);line-height:1;">${esc(c.hnum || '0')}</div>
          ${lblHtml}
        </div>
        ${c.hcap ? `<div style="font-size:12px;color:var(--ink);font-weight:600;">${esc(c.hcap)}</div>` : ''}
        ${c.hsub ? `<div style="font-size:11px;color:color-mix(in srgb,var(--ink) 55%,transparent);font-family:'Cormorant Garamond',serif;font-style:italic;margin-top:1px;">${esc(c.hsub)}</div>` : ''}
        <div style="display:flex;gap:14px;margin-top:6px;font-size:11px;">
          <div style="display:flex;flex-direction:column;">
            <div style="font-family:'PT Serif',Georgia,serif;font-size:15px;font-weight:600;color:var(--ink);line-height:1;">${esc(c.m1n || '0')}</div>
            <div style="font-size:9.5px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:2px;">${esc(c.m1l || '')}</div>
          </div>
          <div style="display:flex;flex-direction:column;">
            <div style="font-family:'PT Serif',Georgia,serif;font-size:15px;font-weight:600;color:var(--ink);line-height:1;">${esc(c.m2n || '0')}</div>
            <div style="font-size:9.5px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:2px;">${esc(c.m2l || '')}</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderStatsBody(resp) {
  const content = $('[data-field="stats-content"]', root());
  const overlay = $('[data-field="stats-overlay"]', root());
  if (!content || !overlay) return;

  if (resp.locked) {
    content.innerHTML = renderLockedSkeleton();
    content.style.filter = 'blur(4px) saturate(.85)';
    content.style.pointerEvents = 'none';
    content.style.userSelect = 'none';
    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div style="background:linear-gradient(155deg,#FBF7EF,#F1E9D8);border:.5px solid rgba(139,94,52,.25);border-radius:16px;padding:18px 20px;text-align:center;box-shadow:0 12px 40px rgba(28,26,22,.18);max-width:300px;">
        <div style="font-size:28px;margin-bottom:8px;">🕯</div>
        <div style="font-family:'PT Serif',Georgia,serif;font-size:15px;font-weight:600;color:var(--ink);margin-bottom:6px;">Жан дос-та ашылады</div>
        <div style="font-size:11px;color:color-mix(in srgb,var(--ink) 55%,transparent);font-family:'Cormorant Garamond',serif;font-style:italic;margin-bottom:12px;line-height:1.6;">
          <b style="color:var(--ac);font-weight:600;font-style:normal;font-family:Onest,sans-serif;">Толық календарь</b> — қай күні қанша оқыдың<br>
          <b style="color:var(--ac);font-weight:600;font-style:normal;font-family:Onest,sans-serif;">Терең талдау</b> — топ күн, кітап, сессия<br>
          <b style="color:var(--ac);font-weight:600;font-style:normal;font-family:Onest,sans-serif;">Ай · Жыл · Бәрі</b> — period бойынша<br>
          <b style="color:var(--ac);font-weight:600;font-style:normal;font-family:Onest,sans-serif;">Айлық есеп</b> — автоматты қорытынды
        </div>
        <button type="button" data-action="open-jandos" style="display:inline-block;padding:8px 16px;background:var(--ac);color:#fff;border:none;border-radius:999px;font-size:11.5px;font-weight:600;cursor:pointer;font-family:Onest,sans-serif;">PARAQTA-ның Жан досы бол → <small style="font-weight:500;opacity:.85;">690₸/ай</small></button>
      </div>
    `;
    return;
  }

  // Unlocked: render grid + insights
  content.style.filter = '';
  content.style.pointerEvents = '';
  content.style.userSelect = '';
  overlay.style.display = 'none';
  overlay.innerHTML = '';
  content.innerHTML = renderStatsGrid(resp.grid) + renderStatsInsights(resp.insights);
}

function renderLockedSkeleton() {
  // Placeholder content under blur — same shape as month for consistency.
  const cells = [];
  for (let i = 0; i < 7; i++) {
    cells.push('<div style="text-align:center;font-size:10px;color:color-mix(in srgb,var(--ink) 40%,transparent);font-weight:600;text-transform:uppercase;padding-bottom:6px;">·</div>');
  }
  for (let i = 0; i < 35; i++) {
    const rand = Math.floor(Math.random() * 5);
    cells.push(`<div style="aspect-ratio:1;border-radius:7px;background:${LVL_BG[rand]};"></div>`);
  }
  return `
    <div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;padding:18px 14px 16px;margin-bottom:10px;">
      <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:5px;">${cells.join('')}</div>
    </div>
    <div style="display:flex;flex-direction:column;gap:8px;">
      ${[1,2,3].map(() => `
        <div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:12px;padding:24px;"></div>
      `).join('')}
    </div>
  `;
}

function renderStatsGrid(g) {
  if (!g) return '';
  const titleBar = `
    <div style="font-size:12px;color:color-mix(in srgb,var(--ink) 55%,transparent);font-weight:600;margin-bottom:14px;display:flex;justify-content:space-between;align-items:center;">
      <span><span style="color:var(--ink);">${esc(g.title || '')}</span> ${esc(g.subtitle || '')}</span>
      <span>${esc(g.right || '')}</span>
    </div>
  `;

  if (g.type === 'today_sessions') {
    return `<div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;padding:18px 14px 16px;margin-bottom:10px;">
      ${titleBar}
      ${renderTodaySessions(g.data)}
    </div>`;
  }
  if (g.type === 'week_strip') {
    return `<div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;padding:18px 14px 16px;margin-bottom:10px;">
      ${titleBar}
      ${renderWeekStrip(g.data)}
      ${renderLegend()}
    </div>`;
  }
  if (g.type === 'month_calendar') {
    return `<div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;padding:18px 14px 16px;margin-bottom:10px;">
      ${titleBar}
      ${renderMonthGrid(g.data, g.leading_blank || 0)}
      ${renderLegend()}
    </div>`;
  }
  if (g.type === 'year_bars') {
    return `<div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;padding:18px 14px 16px;margin-bottom:10px;">
      ${titleBar}
      ${renderYearBars(g.data)}
    </div>`;
  }
  if (g.type === 'all_list') {
    return `<div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:14px;padding:18px 14px 16px;margin-bottom:10px;">
      ${titleBar}
      ${renderAllList(g.data)}
    </div>`;
  }
  return '';
}

function renderTodaySessions(sessions) {
  if (!Array.isArray(sessions) || !sessions.length) {
    return '<div style="text-align:center;padding:20px;color:color-mix(in srgb,var(--ink) 50%,transparent);font-size:12px;font-family:\'Cormorant Garamond\',serif;font-style:italic;">Бүгінгі сессия жоқ. Шамды жағамыз ба?</div>';
  }
  return '<div style="display:flex;flex-direction:column;gap:8px;">' +
    sessions.map((s, i) => `
      <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:rgba(28,26,22,.03);border-radius:10px;">
        <div style="width:32px;height:32px;border-radius:9px;flex-shrink:0;background:linear-gradient(135deg,#C9A96E,#8B5E34);color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;font-family:'PT Serif',Georgia,serif;font-weight:600;">${i+1}</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:12px;font-weight:600;color:var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(s.book || '—')}</div>
          <div style="font-size:10px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:2px;">${esc((s.started_at || '').slice(11,16))} — ${esc((s.ended_at || '').slice(11,16))} · ${s.minutes || 0} мин</div>
        </div>
        <div style="font-family:'PT Serif',Georgia,serif;font-size:16px;font-weight:600;color:var(--ac);">${s.pages || 0}<small style="font-size:10px;font-family:Onest,sans-serif;color:var(--ac);"> бет</small></div>
      </div>
    `).join('') + '</div>';
}

function renderWeekStrip(cells) {
  if (!Array.isArray(cells)) return '';
  const head = cells.map(c => `<div style="text-align:center;font-size:10px;color:color-mix(in srgb,var(--ink) 40%,transparent);font-weight:600;text-transform:uppercase;letter-spacing:.05em;padding-bottom:6px;">${esc(c.weekday)}</div>`).join('');
  const body = cells.map(c => {
    const L = c.level || 0;
    const bg = LVL_BG[L];
    const color = LVL_COLOR[L];
    const todayShadow = c.is_today ? 'box-shadow:0 0 0 1.5px #C9A96E;' : (L === 4 ? 'box-shadow:0 2px 6px rgba(139,94,52,.25);' : '');
    const futOp = c.is_future ? 'opacity:0.3;' : '';
    const pageLine = c.pages > 0 ? `<span style="font-size:11px;font-weight:700;line-height:1.2;margin-top:2px;">${c.pages}</span>` : '';
    return `<div style="aspect-ratio:1;border-radius:7px;display:flex;flex-direction:column;align-items:center;justify-content:center;background:${bg};color:${color};font-size:10px;font-weight:500;${todayShadow}${futOp}">
      <span style="font-size:9px;line-height:1;">${c.day}</span>${pageLine}
    </div>`;
  }).join('');
  return `<div style="display:grid;grid-template-columns:repeat(7,1fr);gap:5px;">${head}${body}</div>`;
}

function renderMonthGrid(cells, leadingBlank) {
  if (!Array.isArray(cells)) return '';
  const wkHead = ['Дс','Сс','Ср','Бс','Жм','Сб','Жс']
    .map(w => `<div style="text-align:center;font-size:10px;color:color-mix(in srgb,var(--ink) 40%,transparent);font-weight:600;text-transform:uppercase;letter-spacing:.05em;padding-bottom:6px;">${w}</div>`).join('');
  const blanks = Array.from({length: leadingBlank}, () => '<div style="aspect-ratio:1;"></div>').join('');
  const body = cells.map(c => {
    const L = c.level || 0;
    const bg = LVL_BG[L];
    const color = LVL_COLOR[L];
    const todayShadow = c.is_today ? 'box-shadow:0 0 0 1.5px #C9A96E;' : (L === 4 ? 'box-shadow:0 2px 6px rgba(139,94,52,.25);' : '');
    const futOp = c.is_future ? 'opacity:0.3;' : '';
    const pageLine = (c.pages > 0 && !c.is_future) ? `<span style="font-size:11px;font-weight:700;line-height:1.2;margin-top:2px;">${c.pages}</span>` : '';
    return `<div style="aspect-ratio:1;border-radius:7px;display:flex;flex-direction:column;align-items:center;justify-content:center;background:${bg};color:${color};font-size:10px;font-weight:500;${todayShadow}${futOp}">
      <span style="font-size:9px;line-height:1;">${c.day}</span>${pageLine}
    </div>`;
  }).join('');
  return `<div style="display:grid;grid-template-columns:repeat(7,1fr);gap:5px;">${wkHead}${blanks}${body}</div>`;
}

function renderYearBars(months) {
  if (!Array.isArray(months) || !months.length) return '';
  const max = Math.max(1, ...months.map(m => m.pages || 0));
  const bars = months.map(m => {
    const h = (m.pages > 0) ? Math.max(8, Math.round((m.pages / max) * 92)) : 4;
    const empty = !m.pages;
    const bg = empty ? 'rgba(28,26,22,.06)' : 'linear-gradient(to top,#8B5E34,#C9A96E)';
    const ring = m.current ? 'box-shadow:0 0 0 1.5px var(--ac);' : '';
    return `<div style="display:flex;flex-direction:column;align-items:center;gap:5px;height:100%;justify-content:flex-end;">
      <div style="width:100%;height:${h}px;background:${bg};border-radius:3px 3px 0 0;${ring}"></div>
      <div style="font-size:9px;color:color-mix(in srgb,var(--ink) 40%,transparent);font-weight:600;">${esc(m.name)}</div>
    </div>`;
  }).join('');
  return `<div style="display:grid;grid-template-columns:repeat(12,1fr);gap:4px;align-items:flex-end;height:120px;">${bars}</div>`;
}

function renderAllList(years) {
  if (!Array.isArray(years) || !years.length) return '<div style="text-align:center;padding:20px;color:color-mix(in srgb,var(--ink) 50%,transparent);font-size:12px;">Деректер жоқ</div>';
  const max = Math.max(1, ...years.map(y => y.pages || 0));
  return '<div style="display:flex;flex-direction:column;gap:8px;">' +
    years.map(y => {
      const w = Math.round(((y.pages || 0) / max) * 100);
      const ringStyle = y.current ? 'background:rgba(139,94,52,.06);' : 'background:rgba(28,26,22,.03);';
      return `<div style="display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;${ringStyle}">
        <div style="font-family:'PT Serif',Georgia,serif;font-size:16px;font-weight:600;color:var(--ac);min-width:48px;">${y.year}${y.current?' <small style="font-size:9px;color:color-mix(in srgb,var(--ink) 55%,transparent);font-family:Onest,sans-serif;font-weight:500;">осы</small>':''}</div>
        <div style="flex:1;height:8px;background:rgba(28,26,22,.05);border-radius:4px;overflow:hidden;"><div style="height:100%;width:${w}%;background:linear-gradient(to right,#8B5E34,#C9A96E);border-radius:4px;"></div></div>
        <div style="font-size:11px;color:color-mix(in srgb,var(--ink) 55%,transparent);min-width:90px;text-align:right;"><b style="color:var(--ink);font-weight:700;">${(y.pages||0).toLocaleString('kk-KZ').replace(/,/g,' ')}</b> бет · ${y.books||0} кітап</div>
      </div>`;
    }).join('') + '</div>';
}

function renderLegend() {
  return `
    <div style="display:flex;align-items:center;justify-content:flex-end;gap:5px;font-size:9.5px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:14px;">
      Аз
      <div style="width:10px;height:10px;border-radius:2px;background:color-mix(in srgb,#8B5E34 18%,transparent);"></div>
      <div style="width:10px;height:10px;border-radius:2px;background:color-mix(in srgb,#8B5E34 42%,transparent);"></div>
      <div style="width:10px;height:10px;border-radius:2px;background:color-mix(in srgb,#8B5E34 65%,transparent);"></div>
      <div style="width:10px;height:10px;border-radius:2px;background:linear-gradient(135deg,#C9A96E,#8B5E34);"></div>
      Көп
    </div>
  `;
}

function renderStatsInsights(insights) {
  if (!Array.isArray(insights) || !insights.length) return '';
  const header = '<div style="font-size:12px;color:var(--ac);font-weight:600;margin:14px 4px 8px;font-family:\'PT Serif\',Georgia,serif;">Айдың сәттері</div>';
  const cards = insights.map(ins => {
    let body = '';
    if (ins.value) body += `<div style="font-family:'PT Serif',Georgia,serif;font-size:14px;font-weight:600;color:var(--ink);line-height:1.25;">${ins.value.replace(/<em>/g,'<em style="font-style:normal;color:var(--ac);">')}</div>`;
    if (ins.meta) body += `<div style="font-size:10.5px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:2px;font-family:'Cormorant Garamond',serif;font-style:italic;">${esc(ins.meta)}</div>`;
    if (ins.rhythm) {
      const rh = ins.rhythm.map(L => {
        const colors = ['rgba(28,26,22,.06)', 'rgba(139,94,52,.25)', 'rgba(139,94,52,.5)', 'linear-gradient(135deg,#C9A96E,#8B5E34)'];
        return `<div style="flex:1;height:14px;border-radius:2px;background:${colors[L]||colors[0]};"></div>`;
      }).join('');
      body += `<div style="display:flex;align-items:center;gap:3px;margin-top:4px;">${rh}</div>`;
    }
    if (ins.books) {
      body += '<div style="display:flex;flex-direction:column;gap:4px;">' +
        ins.books.map(b => `<div style="display:flex;justify-content:space-between;font-size:11.5px;"><b style="font-family:'PT Serif',Georgia,serif;font-weight:600;color:var(--ink);">«${esc(b.title)}»</b><span style="color:color-mix(in srgb,var(--ink) 55%,transparent);">${b.pages} бет</span></div>`).join('') +
        '</div>';
    }
    return `
      <div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:12px;">
        <div style="width:36px;height:36px;border-radius:10px;flex-shrink:0;background:linear-gradient(135deg,rgba(201,169,110,.2),rgba(139,94,52,.06));display:flex;align-items:center;justify-content:center;font-size:16px;">${ins.icon || '·'}</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:11.5px;color:color-mix(in srgb,var(--ink) 55%,transparent);font-weight:500;margin-bottom:2px;">${esc(ins.label || '')}</div>
          ${body}
        </div>
      </div>
    `;
  }).join('');
  return header + `<div style="display:flex;flex-direction:column;gap:8px;">${cards}</div>`;
}

function renderStatsSummary(s) {
  const legend = $('[data-field="heatmap-legend"]', root());
  if (!legend) return;
  if (!s) { legend.innerHTML = ''; return; }
  const cls = s.cmp_cls || 'neutral';
  const color = cls === 'up' ? '#3A5C3C' : (cls === 'down' ? '#A85048' : 'color-mix(in srgb,var(--ink) 55%,transparent)');
  legend.innerHTML = `
    <div style="margin-top:10px;padding:10px 12px;background:color-mix(in srgb,#3A5C3C 6%,transparent);border-radius:10px;display:flex;justify-content:space-between;align-items:center;font-size:11.5px;">
      <span style="font-weight:700;color:var(--ink);">${esc(s.main || '')}</span>
      <span style="color:${color};font-weight:${cls==='neutral'?600:700};">${esc(s.cmp || '')}</span>
    </div>
  `;
}

// ─── Stage 7.6: Жылдық оқу мақсаты (Жан дос) ───
let _lastProfileData = null;
let _goalDraft = { type: 'books', target: 24, lastYear: 0 };

function renderReadingGoal(data) {
  const body = $('[data-field="reading-goal-body"]', root());
  if (!body) return;
  const sub = data?.user?.subscription_status || '';
  const premium = (sub === 'jandos' || sub === 'family_member' || data?.is_premium === true);

  if (!premium) {
    body.innerHTML = `
      <div style="padding:18px;background:linear-gradient(135deg,color-mix(in srgb,var(--gold) 10%,transparent),color-mix(in srgb,var(--ac) 3%,transparent));border:.5px dashed color-mix(in srgb,var(--ac) 30%,transparent);border-radius:16px;text-align:center;">
        <div style="font-size:28px;margin-bottom:8px;">🎯</div>
        <div style="font-family:'PT Serif',Georgia,serif;font-size:15px;font-weight:700;margin-bottom:4px;">Жылдық мақсат — Жан дос</div>
        <div style="font-size:11.5px;color:color-mix(in srgb,var(--ink) 60%,transparent);margin-bottom:12px;font-family:'Cormorant Garamond',serif;font-style:italic;">Жылына қанша кітап оқисың — мақсат қой, прогресіңді бақыла</div>
        <button data-action="open-jandos" style="padding:9px 18px;background:var(--ac);color:#fff;border:none;border-radius:999px;font-size:12px;font-weight:600;cursor:pointer;">PARAQTA-ның Жан досы бол — 690₸</button>
      </div>`;
    return;
  }

  const g = data.reading_goal;
  if (!g) {
    body.innerHTML = `
      <div style="padding:24px 20px;background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:16px;text-align:center;">
        <div style="font-size:32px;margin-bottom:10px;">🎯</div>
        <div style="font-family:'PT Serif',Georgia,serif;font-size:17px;font-weight:700;margin-bottom:6px;">${new Date().getFullYear()} мақсатыңды қой</div>
        <div style="font-size:12px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-bottom:16px;font-family:'Cormorant Garamond',serif;font-style:italic;">Жылына қанша кітап оқығың келеді?</div>
        <button data-action="open-goal-modal" style="padding:11px 24px;background:var(--ac);color:#fff;border:none;border-radius:999px;font-size:13px;font-weight:600;cursor:pointer;">Мақсат қою →</button>
      </div>`;
    return;
  }

  // Active goal — ring + progress + pace
  const circ = 2 * Math.PI * 50; // r=50
  const offset = circ - (Math.min(100, g.pct) / 100) * circ;
  const paceColor = g.pace_status === 'ahead' ? '#3A5C3C' : (g.pace_status === 'behind' ? '#A85048' : '#8B5E34');
  const paceBg = g.pace_status === 'ahead' ? 'rgba(58,92,60,.1)' : (g.pace_status === 'behind' ? 'rgba(168,80,72,.1)' : 'rgba(139,94,52,.1)');
  const paceArrow = g.pace_status === 'ahead' ? '▲' : (g.pace_status === 'behind' ? '▼' : '•');
  body.innerHTML = `
    <div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:16px;padding:20px;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;">
        <div>
          <div style="font-family:'PT Serif',Georgia,serif;font-size:16px;font-weight:700;color:var(--ink);">${g.target} ${esc(g.unit)} оқимын</div>
          <div style="font-size:11px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:2px;font-family:'Cormorant Garamond',serif;font-style:italic;">${g.year} жыл</div>
        </div>
        <button data-action="open-goal-modal" style="background:none;border:none;cursor:pointer;color:var(--ac);font-size:11px;font-weight:600;">Өзгерту</button>
      </div>
      <div style="display:flex;align-items:center;gap:20px;">
        <div style="width:104px;height:104px;flex-shrink:0;position:relative;">
          <svg viewBox="0 0 120 120" style="transform:rotate(-90deg);width:100%;height:100%;">
            <defs><linearGradient id="rg-grad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#C9A96E"/><stop offset="100%" stop-color="#8B5E34"/></linearGradient></defs>
            <circle cx="60" cy="60" r="50" fill="none" stroke="color-mix(in srgb,var(--ink) 7%,transparent)" stroke-width="9"/>
            <circle cx="60" cy="60" r="50" fill="none" stroke="url(#rg-grad)" stroke-width="9" stroke-linecap="round" stroke-dasharray="${circ.toFixed(1)}" stroke-dashoffset="${offset.toFixed(1)}"/>
          </svg>
          <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'PT Serif',Georgia,serif;font-size:24px;font-weight:700;color:var(--ac);">${g.pct}%</div>
        </div>
        <div style="flex:1;">
          <div style="font-family:'PT Serif',Georgia,serif;font-size:30px;font-weight:700;color:var(--ink);line-height:1;">${g.current}<small style="font-size:15px;color:color-mix(in srgb,var(--ink) 55%,transparent);font-family:Onest,sans-serif;font-weight:600;"> / ${g.target}</small></div>
          <div style="font-size:11px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-top:4px;">${esc(g.unit)} оқыдым</div>
          <div style="display:inline-flex;align-items:center;gap:5px;margin-top:12px;padding:5px 12px;border-radius:999px;background:${paceBg};color:${paceColor};font-size:11px;font-weight:700;">${paceArrow} ${esc(g.pace_text)}</div>
        </div>
      </div>
      ${!g.done && g.projected ? `<div style="margin-top:14px;padding-top:12px;border-top:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);font-size:11.5px;color:color-mix(in srgb,var(--ink) 60%,transparent);text-align:center;font-family:'Cormorant Garamond',serif;font-style:italic;">Осы қарқынмен — жыл соңына <b style="color:var(--ac);font-family:'PT Serif',serif;font-style:normal;">${g.projected} ${esc(g.unit)}</b></div>` : ''}
      ${g.done ? `<div style="margin-top:14px;padding-top:12px;border-top:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);font-size:12px;color:var(--gn);text-align:center;font-weight:700;">🎉 Мақсатқа жеттің!</div>` : ''}
    </div>`;
}

// Stage 7.7 — Саған арналған кітаптар (Жан дос). /api/recommendations free → [].
async function renderRecommendations(data) {
  const body = $('[data-field="recommendations-body"]', root());
  if (!body) return;
  const sub = data?.user?.subscription_status || '';
  const premium = (sub === 'jandos' || sub === 'family_member' || data?.is_premium === true);

  if (!premium) {
    body.innerHTML = `
      <div style="padding:18px;background:linear-gradient(135deg,color-mix(in srgb,var(--gold) 10%,transparent),color-mix(in srgb,var(--ac) 3%,transparent));border:.5px dashed color-mix(in srgb,var(--ac) 30%,transparent);border-radius:16px;text-align:center;">
        <div style="font-size:28px;margin-bottom:8px;">📚</div>
        <div style="font-family:'PT Serif',Georgia,serif;font-size:15px;font-weight:700;margin-bottom:4px;">Кітап ұсыныстары — Жан дос</div>
        <div style="font-size:12px;color:color-mix(in srgb,var(--ink) 60%,transparent);margin-bottom:12px;">Сен сияқты оқырмандар ұнатқан кітаптарды саған ұсынамыз</div>
        <button data-action="open-jandos" style="padding:9px 18px;background:var(--ac);color:#fff;border:none;border-radius:999px;font-size:12px;font-weight:600;cursor:pointer;">PARAQTA-ның Жан досы бол — 690₸</button>
      </div>`;
    return;
  }

  body.innerHTML = `<div style="font-size:12px;color:color-mix(in srgb,var(--ink) 45%,transparent);padding:8px 4px;">Ұсыныстар жүктелуде…</div>`;
  let recs;
  try {
    recs = await api('/api/recommendations');
  } catch (e) {
    body.innerHTML = '';
    return;
  }
  if (!Array.isArray(recs) || !recs.length) {
    body.innerHTML = `
      <div style="padding:18px;background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:16px;text-align:center;font-size:12.5px;color:color-mix(in srgb,var(--ink) 55%,transparent);">
        Ұсыныс жасау үшін кемінде 1 кітап оқып бітір 📖
      </div>`;
    return;
  }

  const cards = recs.slice(0, 10).map(r => {
    const cover = r.cover_url
      ? `<div style="width:100%;height:120px;border-radius:10px;background:url('${esc(r.cover_url)}') center/cover no-repeat;"></div>`
      : `<div style="width:100%;height:120px;border-radius:10px;background:linear-gradient(135deg,#C9A96E,#8B5E34);display:flex;align-items:flex-end;padding:8px;box-sizing:border-box;"><span style="color:#fff;font-family:'PT Serif',Georgia,serif;font-size:12px;font-weight:700;line-height:1.2;">${esc(r.title)}</span></div>`;
    const readers = r.readers || r.score;
    return `
      <div data-action="open-book" data-slug="${esc(r.slug || r.title)}" style="flex:0 0 110px;width:110px;cursor:pointer;">
        ${cover}
        <div style="font-size:11.5px;font-weight:600;color:var(--ink);margin-top:6px;line-height:1.25;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${esc(r.title)}</div>
        <div style="font-size:10.5px;color:color-mix(in srgb,var(--ink) 50%,transparent);margin-top:2px;">${esc(r.author || '')}</div>
        ${readers ? `<div style="font-size:10px;color:var(--ac);margin-top:2px;font-weight:600;">${readers} оқырман</div>` : ''}
      </div>`;
  }).join('');

  body.innerHTML = `<div style="display:flex;gap:12px;overflow-x:auto;padding:2px 4px 6px;scrollbar-width:none;-ms-overflow-style:none;">${cards}</div>`;
}

// Stage 7.7 — Атты өзгерту (сайт жағында, user_profiles.display_name)
export function editName() {
  const cur = (_lastProfileData?.user?.name) || '';
  document.querySelector('[data-modal="edit-name"]')?.remove();
  const ov = document.createElement('div');
  ov.dataset.modal = 'edit-name';
  ov.style.cssText = 'position:fixed;inset:0;z-index:9998;background:rgba(28,26,22,.5);display:flex;align-items:center;justify-content:center;padding:24px;';
  ov.innerHTML = `
    <div style="background:var(--bg);border-radius:18px;max-width:360px;width:100%;padding:24px;box-shadow:0 10px 40px rgba(28,26,22,.25);">
      <div style="font-family:'PT Serif',Georgia,serif;font-size:17px;font-weight:700;color:var(--ink);margin-bottom:14px;text-align:center;">Атыңды өзгерт</div>
      <input data-edit-name-input type="text" maxlength="40" value="${esc(cur)}" placeholder="Атың" style="width:100%;padding:12px 14px;border:1px solid color-mix(in srgb,var(--ink) 15%,transparent);border-radius:12px;font-size:15px;font-family:'Onest',sans-serif;background:var(--card);color:var(--ink);outline:none;margin-bottom:14px;box-sizing:border-box;">
      <div style="display:flex;gap:10px;">
        <button data-edit-name-cancel style="flex:1;padding:12px;background:color-mix(in srgb,var(--ink) 8%,transparent);color:var(--ink);border:none;border-radius:999px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Onest',sans-serif;">Болдырмау</button>
        <button data-edit-name-save style="flex:1;padding:12px;background:var(--ac);color:#fff;border:none;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer;font-family:'Onest',sans-serif;">Сақтау</button>
      </div>
    </div>`;
  const input = ov.querySelector('[data-edit-name-input]');
  const close = () => ov.remove();
  ov.addEventListener('click', (e) => { if (e.target === ov || e.target.hasAttribute('data-edit-name-cancel')) close(); });
  ov.querySelector('[data-edit-name-save]').addEventListener('click', async () => {
    const name = (input.value || '').trim();
    if (!name) { toast('Ат бос болмауы керек'); return; }
    try {
      await api('/api/profile/name', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      toast('Атың өзгертілді ✓');
      close();
      loadProfile();
    } catch (e) { toast('Қате болды'); }
  });
  document.body.appendChild(ov);
  setTimeout(() => { try { input.focus(); } catch (_) {} }, 50);
}

export function openGoalModal() {
  const data = _lastProfileData || {};
  const g = data.reading_goal;
  _goalDraft = {
    type: g?.goal_type || 'books',
    target: g?.target || 24,
    lastYear: data.last_year_books || 0,
  };
  // Fetch last_year suggestion if not present
  renderGoalModal();
}

function renderGoalModal() {
  document.querySelector('[data-modal="reading-goal"]')?.remove();
  const d = _goalDraft;
  const unit = d.type === 'books' ? 'кітап' : 'бет';
  const step = d.type === 'books' ? 1 : 10;
  const hint = d.type === 'books' && d.lastYear > 0
    ? `Өткен жылы ${d.lastYear} кітап оқыдың`
    : 'Соңынан өзгерте аласың';
  const overlay = document.createElement('div');
  overlay.dataset.modal = 'reading-goal';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(28,26,22,.55);z-index:9999;display:flex;align-items:flex-end;justify-content:center;';
  overlay.innerHTML = `
    <div style="background:var(--bg);border-radius:18px 18px 0 0;padding:24px 20px 28px;max-width:400px;width:100%;">
      <div style="font-family:'PT Serif',Georgia,serif;font-size:17px;font-weight:700;text-align:center;margin-bottom:4px;">${new Date().getFullYear()} мақсатың</div>
      <div style="font-size:12px;color:color-mix(in srgb,var(--ink) 55%,transparent);text-align:center;margin-bottom:18px;font-family:'Cormorant Garamond',serif;font-style:italic;">${esc(hint)}</div>
      <div style="display:flex;gap:8px;margin-bottom:18px;">
        <button data-action="goal-type" data-type="books" style="flex:1;padding:12px;border-radius:12px;cursor:pointer;border:1px solid ${d.type==='books'?'var(--ac)':'color-mix(in srgb,var(--ink) 12%,transparent)'};background:${d.type==='books'?'color-mix(in srgb,var(--ac) 6%,transparent)':'transparent'};font-family:Onest,sans-serif;">
          <div style="font-family:'PT Serif',serif;font-size:14px;font-weight:700;color:var(--ink);">📚 Кітап</div>
        </button>
        <button data-action="goal-type" data-type="pages" style="flex:1;padding:12px;border-radius:12px;cursor:pointer;border:1px solid ${d.type==='pages'?'var(--ac)':'color-mix(in srgb,var(--ink) 12%,transparent)'};background:${d.type==='pages'?'color-mix(in srgb,var(--ac) 6%,transparent)':'transparent'};font-family:Onest,sans-serif;">
          <div style="font-family:'PT Serif',serif;font-size:14px;font-weight:700;color:var(--ink);">📖 Бет</div>
        </button>
      </div>
      <div style="display:flex;align-items:center;justify-content:center;gap:18px;margin-bottom:6px;">
        <button data-action="goal-step" data-delta="-${step}" style="width:44px;height:44px;border-radius:50%;border:1px solid color-mix(in srgb,var(--ink) 12%,transparent);background:var(--card);font-size:22px;color:var(--ac);cursor:pointer;">−</button>
        <div data-field="goal-val" style="font-family:'PT Serif',Georgia,serif;font-size:42px;font-weight:700;color:var(--ac);min-width:90px;text-align:center;">${d.target}</div>
        <button data-action="goal-step" data-delta="${step}" style="width:44px;height:44px;border-radius:50%;border:1px solid color-mix(in srgb,var(--ink) 12%,transparent);background:var(--card);font-size:22px;color:var(--ac);cursor:pointer;">+</button>
      </div>
      <div style="text-align:center;font-size:12px;color:color-mix(in srgb,var(--ink) 55%,transparent);margin-bottom:18px;">${unit} / жыл</div>
      <button data-action="goal-save" style="width:100%;padding:13px;background:var(--ac);color:#fff;border:none;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer;font-family:Onest,sans-serif;">Мақсатты сақтау</button>
      <button data-action="goal-close" style="width:100%;padding:10px;background:transparent;color:color-mix(in srgb,var(--ink) 55%,transparent);border:none;font-size:12px;cursor:pointer;margin-top:4px;font-family:Onest,sans-serif;">Жабу</button>
    </div>`;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
    const a = e.target.closest('[data-action]')?.dataset?.action;
    if (a === 'goal-close') overlay.remove();
    else if (a === 'goal-type') { _goalDraft.type = e.target.closest('[data-type]').dataset.type; if (_goalDraft.type==='pages' && _goalDraft.target < 50) _goalDraft.target = 300; if (_goalDraft.type==='books' && _goalDraft.target > 200) _goalDraft.target = 24; renderGoalModal(); }
    else if (a === 'goal-step') {
      const delta = parseInt(e.target.closest('[data-delta]').dataset.delta, 10);
      const max = _goalDraft.type === 'pages' ? 100000 : 500;
      _goalDraft.target = Math.max(1, Math.min(max, _goalDraft.target + delta));
      const v = overlay.querySelector('[data-field="goal-val"]'); if (v) v.textContent = _goalDraft.target;
    }
    else if (a === 'goal-save') { saveReadingGoal(); overlay.remove(); }
  });
  document.body.appendChild(overlay);
}

async function saveReadingGoal() {
  try {
    const r = await api('/api/profile/goal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ goal_type: _goalDraft.type, target: _goalDraft.target }),
    });
    if (r?.status === 'ok') {
      toast('🎯 Мақсат сақталды');
      loadProfile(); // re-render with new goal
    } else if (r?.status === 'premium_required') {
      toast('Жан дос мүмкіндігі');
    } else {
      toast('Сақталмады');
    }
  } catch (e) {
    toast('Желі қатесі');
  }
}

function renderAchievements(data) {
  const a = data.achievements || {};
  setField('achievements-count', `${a.earned ?? 0} / ${a.total ?? 0}`);
  const wrap = $('[data-field="achievements-list"]', root());
  if (!wrap) return;
  wrap.innerHTML = '';

  const items = Array.isArray(a.list) ? a.list : [];
  // Жаңа ашылған жетістік мерекесі — жалғыз дереккөзден (04.10.26).
  import('../celebrations/index.js').then(m => m.maybeCelebrateAchievements(items)).catch(() => {});
  items.forEach(it => {
    // Stage 8 v2: мокап белгісі — 52px дөңгелек-шаршы + жапсырма
    const badge = document.createElement('div');
    badge.style.cssText = 'flex: 0 0 auto; width: 64px; text-align: center;' + (it.earned ? '' : ' opacity: 0.5;');
    const tile = it.earned
      ? 'background: linear-gradient(150deg, color-mix(in srgb, var(--gold) 32%, transparent), color-mix(in srgb, var(--ac) 12%, transparent));'
      : 'background: color-mix(in srgb, var(--ink) 6%, transparent); filter: grayscale(1);';
    badge.innerHTML = `
      <div style="width: 52px; height: 52px; margin: 0 auto; border-radius: 15px; display: flex; align-items: center; justify-content: center; font-size: 24px; ${tile}">${esc(it.icon || '🔒')}</div>
      <div style="font-size: 9.5px; color: color-mix(in srgb, var(--ink) 62%, transparent); margin-top: 5px; line-height: 1.1;">${esc(it.title || '')}</div>
    `;
    wrap.appendChild(badge);
  });
}

// Stage 7.7 — Менің жазбаларым: Ой + Дәйексөз + Рецензия (бір блок, табтар).
let _writingsTab = 'thoughts';
const _WTABS = [
  { key: 'thoughts', label: 'Ойларым',     icon: '💭', url: '/api/thoughts/list?limit=30', field: 'thoughts',
    empty: 'Әзірге ой жоқ. Оқу сессиясын аяқтағанда «Түйген ойды жазу» бас.' },
  { key: 'quotes',   label: 'Дәйексөз',     icon: '📌', url: '/api/my/quotes?limit=40',     field: 'quotes',
    empty: 'Әзірге дәйексөз жоқ. Кітап бетінде сақта.' },
  { key: 'reviews',  label: 'Рецензиялар',  icon: '✍️', url: '/api/my/reviews?limit=40',    field: 'reviews',
    empty: 'Әзірге рецензия жоқ. Кітап бетінде жаз.' },
];

function _fmtWritingDate(s) {
  const m = String(s || '').match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
}

function _writingCard(icon, bookTitle, text, createdAt) {
  const full = String(text || '');
  const snippet = full.slice(0, 260) + (full.length > 260 ? '…' : '');
  return `<div style="background:var(--card);border:.5px solid color-mix(in srgb,var(--ink) 8%,transparent);border-radius:12px;padding:13px 14px;margin-bottom:8px;">
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
      <span style="font-size:13px;">${icon}</span>
      <span style="font-size:11.5px;font-weight:600;color:var(--ac);">${esc(bookTitle || 'Кітап')}</span>
    </div>
    <div style="font-size:13px;line-height:1.55;color:color-mix(in srgb,var(--ink) 80%,transparent);">${esc(snippet)}</div>
    ${createdAt ? `<div style="font-size:10px;color:color-mix(in srgb,var(--ink) 40%,transparent);margin-top:7px;">${esc(_fmtWritingDate(createdAt))}</div>` : ''}
  </div>`;
}

function _writingEmpty(msg) {
  return `<div style="padding:22px 16px;text-align:center;font-size:12.5px;line-height:1.5;color:color-mix(in srgb,var(--ink) 50%,transparent);background:var(--card);border:.5px dashed color-mix(in srgb,var(--ink) 12%,transparent);border-radius:12px;">${esc(msg)}</div>`;
}

async function _loadWritingsTab(tab) {
  const body = $('[data-field="writings-body"]', root());
  if (!body) return;
  const t = _WTABS.find(x => x.key === tab) || _WTABS[0];
  body.innerHTML = `<div style="font-size:12px;color:color-mix(in srgb,var(--ink) 45%,transparent);padding:8px 4px;">Жүктелуде…</div>`;
  let items = [];
  try {
    const r = await api(t.url);
    items = (r && r[t.field]) || [];
  } catch (e) { body.innerHTML = _writingEmpty('Жүктелмеді.'); return; }
  body.innerHTML = items.length
    ? items.map(it => _writingCard(t.icon, it.book_title, it.text, it.created_at)).join('')
    : _writingEmpty(t.empty);
}

function renderMyWritings() {
  const rootEl = $('[data-field="writings-root"]', root());
  if (!rootEl) return;
  const tabsHtml = _WTABS.map(t => {
    const on = t.key === _writingsTab;
    return `<button data-wtab="${t.key}" style="flex:1;padding:8px 6px;border:none;border-radius:10px;font-size:12px;font-weight:600;cursor:pointer;font-family:'Onest',sans-serif;${on ? 'background:var(--ac);color:#fff;' : 'background:color-mix(in srgb,var(--ink) 6%,transparent);color:color-mix(in srgb,var(--ink) 60%,transparent);'}">${t.label}</button>`;
  }).join('');
  rootEl.innerHTML = `
    <div style="font-size:10px;letter-spacing:1.5px;color:var(--ac);font-weight:500;text-transform:uppercase;margin-bottom:10px;padding:0 4px;">Менің жазбаларым</div>
    <div style="display:flex;gap:6px;margin-bottom:12px;">${tabsHtml}</div>
    <div data-field="writings-body"></div>`;
  rootEl.querySelectorAll('[data-wtab]').forEach(b => {
    b.addEventListener('click', () => {
      if (_writingsTab === b.dataset.wtab) return;
      _writingsTab = b.dataset.wtab;
      renderMyWritings();
    });
  });
  _loadWritingsTab(_writingsTab);
}

// ❗31.08.26 ҚАЙТА АШЫЛДЫ. Бұрын бұл функция екі картаны да мәжбүрлеп
// жасыратын («есеп тек Бүгін бетінде көрінеді» деген себеппен) — ал ол бет
// навигациядан алынған соң есеп ЕШҚАЙДА көрінбей қалды.
// Енді: Оқу бетінде ай басында 3 күн баннер (бір рет ашса кетеді), ал
// ТҰРАҚТЫ орны — осы жерде, профильде. Көріну ережесін `applyVisibility`
// шешеді (`data-visibility="premium-only" / "free-only"`), сондықтан бұл
// функция тек САНДЫ толтырады.
function renderWrapped(data) {
  const w = data.wrapped_monthly;
  if (!w) return;                       // тегін адам: locked картасы тұрады
  const hdr = $('[data-field="wrapped-header"]', root());
  if (hdr && w.month_name) hdr.textContent = `${w.month_name} айының есебі`;
  setField('wrapped-pages', w.pages ?? 0);
  setField('wrapped-books', w.books ?? 0);
  setField('wrapped-genre', w.favorite_genre || '—');
}

function renderSettings(data) {
  const s = data.settings || {};
  setField('app-version', data.app_version || '—');
  // Spoiler toggle
  const tog = $('[data-field="spoiler-toggle"]', root());
  const knob = $('[data-field="spoiler-knob"]', root());
  if (tog) {
    const on = s.auto_hide_spoiler !== false;
    tog.dataset.on = on ? '1' : '0';
    tog.style.background = on ? '#3A5C3C' : 'color-mix(in srgb, var(--ink) 15%, transparent)';
    if (knob) {
      knob.style.right = on ? '2px' : 'auto';
      knob.style.left = on ? 'auto' : '2px';
    }
  }
  // Jandos status pill
  const pill = $('[data-field="jandos-status"]', root());
  if (pill) {
    if (s.jandos_active) {
      pill.textContent = s.jandos_until ? `${s.jandos_until} дейін` : 'Active';
      pill.style.background = 'color-mix(in srgb, var(--gn) 10%, transparent)';
      pill.style.color = '#3A5C3C';
    } else {
      pill.textContent = 'Тіркелу';
      pill.style.background = 'color-mix(in srgb, var(--ac) 10%, transparent)';
      pill.style.color = '#8B5E34';
    }
  }
}

// ── Actions ─────────────────────────────────────────────────
export function profileBack() {
  show('oqu');
}

export function profileSettings() {
  const sec = $('[data-section="settings"]', root());
  if (sec && sec.scrollIntoView) sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function copyUrl() {
  const el = $('[data-field="user-url"]', root());
  const txt = el?.textContent?.trim();
  if (!txt) return;
  const url = txt.startsWith('http') ? txt : `https://${txt}`;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).then(
      () => toast('🔗 Сілтеме көшірілді'),
      () => toast('Көшіру мүмкін болмады'),
    );
  } else {
    toast(url);
  }
}

export function openList(status) {
  // Профильдегі тізім → Кітаптарым бетіне өтіп, тиісті сүзгіні қолдану.
  // status (бот DB): finished → 2, reading → 1, wishlist → 0.
  // 04.10: ескі «Кітаптарым» (books) беті өшті → «Менің сөрем» (3D сөре).
  void status;
  show('shelf');
}

export function openQuote() {
  toast('💭 Дәйексөз модалі — жақын арада');
}

export function openAchievements() {
  show('achieve');
}

export function openQuotes() {
  toast('📚 Дәйексөз тізімі — жақын арада');
}

export async function openWrapped() {
  // Stage 6.9.5 — Premium check (cached profile data), сосын Wrapped page-ке
  try {
    const me = await api('/api/profile/me');
    const sub = me?.user?.subscription_status;
    const premium = (sub === 'jandos' || sub === 'family_member');
    if (!premium) {
      const { showPremiumLockModal } = await import('../premium-lock/modal.js');
      showPremiumLockModal('wrapped');
      return;
    }
    show('wrapped');
  } catch (e) {
    console.error('openWrapped failed', e);
    const { showPremiumLockModal } = await import('../premium-lock/modal.js');
    showPremiumLockModal('wrapped');
  }
}

export function settingsAccount()       { import('../account/index.js').then(m => m.openAccount()).catch(() => toast('Ашылмады — қайта байқап көр')); }
export function settingsNotifications() {
  import('../push/index.js')
    .then(m => m.enablePush('🔔 Хабарламалар қосылды — квиз, streak және оқу ескертулерін аласың'))
    .catch(() => toast('Қосу сәтсіз — қайталап көріңіз'));
}
export function settingsPrivacy()       { import('../account/index.js').then(m => m.openPrivacy()).catch(() => window.open('/privacy.html', '_blank')); }
export function settingsHelp()          { import('../account/index.js').then(m => m.openHelp()).catch(() => toast('Ашылмады — қайта байқап көр')); }
export function settingsCopyright() {
  alert('Авторлық құқық туралы\n\n'
    + 'Жүктеген кітап файлдарың ЖЕКЕ сақталады — оларды тек сен көресің әрі оқисың. '
    + 'PARAQTA файлдарды басқа қолданушыларға таратпайды.\n\n'
    + 'Жүктеген файлыңа өзің жауаптысың.\n\n'
    + 'Авторлық құқық иесісіз бе, шағымыңыз бар ма?\n→ copyright@paraqta.kz');
}
export function settingsJandos()        { show('jandos'); /* Stage 6.7: /жан-дос беті */ }

export function settingsSpoiler() {
  // Toggle UI-ды flip (backend сақтау қазір жоқ — localStorage қолданамыз)
  const tog = $('[data-field="spoiler-toggle"]', root());
  const knob = $('[data-field="spoiler-knob"]', root());
  if (!tog) return;
  const on = tog.dataset.on !== '1';
  tog.dataset.on = on ? '1' : '0';
  tog.style.background = on ? '#3A5C3C' : 'color-mix(in srgb, var(--ink) 15%, transparent)';
  if (knob) {
    knob.style.right = on ? '2px' : 'auto';
    knob.style.left = on ? 'auto' : '2px';
  }
  try {
    localStorage.setItem('paraqta_auto_hide_spoiler', on ? '1' : '0');
  } catch (_) {}
  toast(on ? '🚫 Спойлер автоматты жасырылады' : '👁 Спойлер ашық');
}

// ── Main entry ──────────────────────────────────────────────
let _loading = false;
export async function loadProfile() {
  // Шкаф сервері қосулы болса ғана кіру батырмасы көрінеді (WARDROBE=1)
  fetch('/api/wardrobe', { credentials: 'include' }).then(r => r.ok).then(ok => {   // 3D модульді жүктемей тексереміз
    const b = document.getElementById('wd-entry');
    if (b && ok) { b.hidden = false; b.style.display = 'flex'; }
  }).catch(() => {});
  if (_loading) return;
  _loading = true;
  try {
    const data = await api('/api/profile/me');
    if (!data?.user) {
      toast('Профиль жүктелмеді');
      return;
    }
    _lastProfileData = data;  // Stage 7.6: goal modal үшін
    applyVisibility(data);
    renderHero(data);
    renderStats(data);
    renderReadingNow(data);
    renderLists(data);
    renderHeatmap(data);
    renderReadingGoal(data);
    renderRecommendations(data);  // Stage 7.7: Жан дос ұсыныстары
    renderAchievements(data);
    renderMyWritings();  // Stage 7.7: Ой + Дәйексөз + Рецензия табтары
    renderWrapped(data);
    renderSettings(data);
  } catch (e) {
    console.error('loadProfile failed', e);
    toast('Профиль жүктелмеді');
  } finally {
    _loading = false;
  }
}

